const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface FocusTrapOptions {
  onEscape?: () => void;
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

export function useFocusTrap() {
  let _keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  let _returnFocusEl: HTMLElement | null = null;

  function activate(container: HTMLElement, options: FocusTrapOptions = {}) {
    deactivate(); // clean up any existing trap before activating a new one

    _returnFocusEl = document.activeElement as HTMLElement | null;

    _keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        options.onEscape?.();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = getFocusable(container);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', _keydownHandler);

    const focusable = getFocusable(container);
    (focusable[0] ?? container).focus();
  }

  function deactivate() {
    if (_keydownHandler) {
      document.removeEventListener('keydown', _keydownHandler);
      _keydownHandler = null;
    }
    if (_returnFocusEl && document.contains(_returnFocusEl)) {
      _returnFocusEl.focus();
    }
    _returnFocusEl = null;
  }

  return { activate, deactivate };
}
